'use client';

/**
 * PD-35977 — HomepageV3 live-offering adapter.
 *
 * The SE3 editorial homepage (HomepageV3) used to render hardcoded `demoData`
 * (`SECTIONS` / `COMPANIES` / spotlights). That is the A/B *test* variant of
 * the control homepage, so it must show the SAME real offerings the control
 * homepage shows.
 *
 * This hook reuses the control data path exactly:
 *   `useGetCurrentOfferingsTombstones` → `/primary_facade/v2/offerings/current/tombstones`
 *   → `transformPrimaryOfferings` (the same transform control uses)
 * and maps the live offerings into the SE3 `OfferingCard` shape
 * (`OfferingSection[]` of `OfferingCardData` + a `Company` lookup map).
 *
 * Control row → SE3 shelf mapping:
 *   - Trending now   ← `most_momentum_se3` when present/non-empty, else
 *                       `most_momentum` (PD-35981 SE3 A/B ranking; incl Reg D).
 *   - Pre-IPO Deals  ← Reg D offerings across the live rows, ranked by
 *                       momentum and shown directly beneath Trending now.
 *   - Closing soon   ← `closing_soon_se3` when present/non-empty, else
 *                       `closing_soon` (PD-35981 SE3 A/B ranking; two-tier).
 *   - Low minimum    ← FE synthesis (PD-35973): crowdfunding-only (Reg CF /
 *                       Reg A) subset of the same preferred momentum row used
 *                       by Trending, capped at 4.
 *
 * Row rules:
 *   - Full "View all" shelves are independent category lenses. An offering may
 *     appear in more than one full list when it qualifies for each category.
 *   - Duplicate slugs within a shelf are removed.
 *   - Homepage previews claim each offering once, in shelf order. Later
 *     previews continue through their complete candidate list to backfill.
 *   - 4 tombstones per shelf (`MAX_CARDS_PER_SHELF`).
 *
 * Badges (PD-35974 — frontend render only):
 *   - On-card badge: "X days left" (from `daysLeft`) and "Waitlist" (from
 *     `waitlist`). Days-left takes precedence when both apply.
 *   - Under-card label (`meta`): "Qualified Purchasers Exclusive" /
 *     "IRA Exclusive" / "Previously Sold Out" — rendered ONLY when the
 *     corresponding eligibility flag is present on the offering. Those flags
 *     are NOT yet provided by the tombstones endpoint / `transformPrimaryOfferings`
 *     (see TransformedOffering optional fields) — until the backend ticket
 *     surfaces them, nothing renders (graceful no-op).
 *   - Crowdfunding (Reg CF / Reg A): ALL badges + labels suppressed.
 *
 * Graceful fallback: when the hook is loading / errors / returns empty, the
 * consumers fall back to the static `demoData` content so the hero/rows never
 * blank (SSR-safe — the transform + mapping are pure and run with no data on
 * the server). See `OfferingSections` / `OfferingSpotlight` wiring.
 */

import { useMemo } from 'react';

import useGetCurrentOfferingsTombstones from 'hooks/useGetCurrentOfferingsTombstones';
import useGetOffering from 'hooks/useGetOffering';
import useExcludeAmountOfferingList from 'hooks/useExcludeAmountOfferingList';
import useShowAmountRaisedList from 'hooks/useShowAmountRaisedList';
import { useSafeFeatureFlagPayload } from 'hooks/useSafeFeatureFlag';
import {
  ELIGIBILITY_BADGES_FLAG,
  parseEligibilityBadges,
} from 'components/HomepageV3/data/eligibilityBadges';
import { transformPrimaryOfferings } from 'components/Homepage/transforms';
import { isRegA, isRegCf, isRegD, regDDisclosure } from 'components/HomepageV3/utils/disclosures';
import { filterHiddenOfferings, isHiddenSlug } from 'utils/offerings/hiddenSlug';
import { formatCurrencyFlexible, formatCurrencyToShorthand } from 'utils/helpers';
import {
  shouldShowAmountRaised,
  type AmountVisibilityContext,
} from 'utils/offerings/amountRaisedVisibility';
import type { Company, OfferingCardData, OfferingSection, Spotlight } from './demoData';
import usePrivatePageOfferings, { type PrivatePageOfferings } from './usePrivatePageOfferings';
import { allocatePreviewSections } from '../utils/previewSections';

/** A single transformed offering (subset of `transformPrimaryOfferings` output). */
interface TransformedOffering {
  id?: string;
  name?: string;
  content?: string;
  industry?: string;
  category?: string;
  categories?: Array<{ name?: string; label?: string } | string>;
  sector?: string;
  tags?: string[];
  coverTombstoneUrl?: string | null;
  logoUrl?: string | null;
  regulationType?: string;
  amountRaised?: string;
  maximumFundingAmount?: string;
  lockedOnWaitlist?: boolean;
  companyName?: string;
  numberOfInvestors?: number;
  minimumInvestmentAmount?: string;
  endDate?: string;
  daysLeft?: number;
  slug?: string;
  percentCompleted?: number;
  fundsCommitted?: string;
  status?: string;
  /**
   * Eligibility / availability flags (PD-35974). These are NOT currently
   * emitted by the tombstones endpoint or `transformPrimaryOfferings`; they are
   * declared optional so the badge logic can render them the moment the backend
   * starts providing them, and renders nothing in the meantime. See the PR body
   * for the exact backend fields still required.
   */
  waitlist?: boolean;
  qpExclusive?: boolean;
  iraExclusive?: boolean;
  soldOut?: boolean;
}

interface FetchedFeaturedOffering {
  id?: string | number;
  name?: string;
  content?: string;
  tagline?: string;
  industry?: string;
  category?: string;
  sector?: string;
  tags?: string[];
  coverTombstoneUrl?: string | null;
  startup_cover_url?: string | null;
  regulationType?: string;
  investment_type_pretty?: string;
  amountRaised?: string | number;
  maximumFundingAmount?: string | number;
  maximum_funding_amount?: string | number;
  lockedOnWaitlist?: boolean;
  locked_on_waitlist?: boolean;
  fundsCommitted?: string | number;
  funds_committed?: string | number;
  public_funds_committed?: string | number;
  numberOfInvestors?: number | string;
  public_number_of_contributers?: number | string;
  minimumInvestmentAmount?: string | number;
  minimum_investment_amount_pretty?: string | number;
  endDate?: string;
  funding_end_date?: string;
  daysLeft?: number | string;
  days_left?: number | string;
  slug?: string;
  link?: string;
  percentCompleted?: number | string;
  percent_completed?: number | string;
  status?: string;
  waitlist?: boolean;
  qpExclusive?: boolean;
  iraExclusive?: boolean;
  soldOut?: boolean;
}

interface TransformedTombstones {
  most_momentum?: TransformedOffering[];
  most_popular?: TransformedOffering[];
  newest?: TransformedOffering[];
  closing_soon?: TransformedOffering[];
  /**
   * PD-35981 — SE3 A/B ranking rows. Backend (primary-facade PR #1195) adds
   * these additively to the SAME `/tombstones` response. The SE3 adapter
   * prefers them (Trending ← `most_momentum_se3`, Closing soon ←
   * `closing_soon_se3`) when present and non-empty, and falls back to the
   * legacy `most_momentum` / `closing_soon` rows when absent. The control
   * homepage continues to read only the legacy rows.
   */
  most_momentum_se3?: TransformedOffering[];
  closing_soon_se3?: TransformedOffering[];
}

export interface HomepageV3LiveCompany extends Company {
  /** `/offering/<slug>` link for the live offering. */
  href?: string;
  /** Numeric minimum investment (USD), used to bucket the Minimum filter. */
  minimumAmount?: number;
}

export interface HomepageV3Offerings {
  /** Live offering shelves, ready to pass to `OfferingSections`. */
  sections: OfferingSection[];
  /** Uncapped live offerings for SE3 Explore search results. */
  searchSections: OfferingSection[];
  /**
   * PD-36468 — per-category shelves, UNCAPPED and without cross-shelf dedup, so
   * a "View all <category>" page lists every offering in that category (not the
   * 4-card homepage preview). Same titles as `sections` so the sidebar
   * selection resolves against them.
   */
  fullSections: OfferingSection[];
  /** Company lookup keyed by `OfferingCardData.co` (the offering slug). */
  companies: Record<string, HomepageV3LiveCompany>;
  /** Admin-configured eligibility labels keyed by normalized offering slug. */
  eligibilityBadges: Record<string, string>;
  /** Featured spotlight derived from the live set (or null when none). */
  spotlight: Spotlight | null;
  /** `/offering/<slug>` link for the featured spotlight (or undefined). */
  spotlightHref?: string;
  /** Raw remote campaign image for the spotlight `default` variant. */
  spotlightImage?: string;
  /** Slug→href map for card navigation (keyed by `OfferingCardData.co`). */
  hrefByCo: Record<string, string>;
  isLoading: boolean;
  /** True when the live data resolved to at least one usable offering. */
  hasLiveData: boolean;
  amountVisibility?: AmountVisibilityContext;
}

// Curated homepage featured/spotlight offering (Josh request 2026-07-10):
// PsiQuantum (accredited, Reg D). Cleared to feature on the public homepage.
const HOMEPAGE_FEATURED_OFFERING_SLUG = 'psiquantum-4';

/**
 * PostHog config flag whose JSON payload is a dynamic list of offering slugs to
 * hide from SE3 browse — ops-controllable with no deploy (e.g. an offering in a
 * VC early-access window). Payload may be a string[], a comma-string, or
 * `{ slugs: string[] }`.
 */
const HIDDEN_SLUGS_FLAG = 'se3-hidden-offering-slugs';

const parseHiddenSlugs = (payload: unknown): string[] => {
  if (Array.isArray(payload)) {
    return payload.filter((s): s is string => typeof s === 'string');
  }
  if (typeof payload === 'string') {
    return payload.split(',');
  }
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { slugs?: unknown }).slugs)
  ) {
    return (payload as { slugs: unknown[] }).slugs.filter(
      (s): s is string => typeof s === 'string',
    );
  }
  return [];
};

/**
 * PostHog flag for per-offering eligibility badge overrides (the pill above the
 * card body). Lets ops add a badge to a tombstone with no deploy — mirrors the
 * HIDDEN_SLUGS_FLAG pattern. Payload is a slug -> badge-type map, e.g.
 *   { "openai-qp-1": "qp", "acme-ira": "ira", "old-fund": "soldOut" }
 * `iraExclusive` / `soldOut` also derive automatically from offering flags when
 * the backend emits them; this flag is the manual lever (and the only way to set
 * "Qualified Purchasers Exclusive" until the backend provides qpExclusive).
 */
/** Reg line shown beneath the card, mirroring control's TombstoneV2 copy. */
const regLine = (reg?: string): string => {
  if (isRegD(reg)) return 'Reg D via StartEngine Primary, LLC';
  return `${reg || 'Reg CF'} via StartEngine Primary, LLC`;
};

const normalizeDisplayText = (value?: string): string => value?.trim().replace(/\s+/g, ' ') || '';

const stringValue = (value?: string | number | null): string | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  const normalized = value?.trim();
  return normalized || undefined;
};

const numberValue = (value?: string | number | null): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const parsed = Number.parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeFetchedFeaturedOffering = (
  offering: FetchedFeaturedOffering | null | undefined,
): TransformedOffering | null => {
  if (!offering) return null;

  const slug = stringValue(offering.slug ?? offering.link);
  const name = stringValue(offering.name);
  if (!slug || !name) return null;

  const fundsCommitted = stringValue(
    offering.fundsCommitted ?? offering.funds_committed ?? offering.public_funds_committed,
  );

  return {
    id: stringValue(offering.id),
    name,
    content: stringValue(offering.content ?? offering.tagline),
    industry: stringValue(offering.industry),
    category: stringValue(offering.category),
    sector: stringValue(offering.sector),
    tags: Array.isArray(offering.tags)
      ? offering.tags.filter((tag): tag is string => typeof tag === 'string')
      : undefined,
    coverTombstoneUrl:
      stringValue(offering.coverTombstoneUrl ?? offering.startup_cover_url) ?? null,
    regulationType: stringValue(offering.regulationType ?? offering.investment_type_pretty),
    amountRaised: stringValue(offering.amountRaised) ?? fundsCommitted,
    maximumFundingAmount: stringValue(
      offering.maximumFundingAmount ?? offering.maximum_funding_amount,
    ),
    numberOfInvestors: numberValue(
      offering.numberOfInvestors ?? offering.public_number_of_contributers,
    ),
    minimumInvestmentAmount: stringValue(
      offering.minimumInvestmentAmount ?? offering.minimum_investment_amount_pretty,
    ),
    endDate: stringValue(offering.endDate ?? offering.funding_end_date),
    daysLeft: numberValue(offering.daysLeft ?? offering.days_left),
    slug,
    percentCompleted: numberValue(offering.percentCompleted ?? offering.percent_completed),
    fundsCommitted,
    status: stringValue(offering.status),
    lockedOnWaitlist: offering.lockedOnWaitlist ?? offering.locked_on_waitlist,
    waitlist: offering.waitlist,
    qpExclusive: offering.qpExclusive,
    iraExclusive: offering.iraExclusive,
    soldOut: offering.soldOut,
  };
};

/** Series/eyebrow line for the card head. */
const seriesLine = (offering: TransformedOffering): string => {
  const reg = offering.regulationType;
  if (isRegA(reg)) return 'Reg A+';
  return reg || 'Reg CF';
};

const sectorLine = (offering: TransformedOffering): string => {
  const candidates = [
    offering.industry,
    offering.category,
    offering.sector,
    ...(Array.isArray(offering.tags) ? offering.tags : []),
  ];
  const desc = normalizeDisplayText(offering.content).toLowerCase();
  return (
    candidates
      .map(normalizeDisplayText)
      .find((candidate) => candidate && candidate.toLowerCase() !== desc) || ''
  );
};

const parseAmount = (value?: string): number => {
  if (!value) return NaN;
  return Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
};

/** Short money string (e.g. "$1.2M") for the "Invested" figure. */
const investedDisplay = (offering: TransformedOffering): string => {
  const amount = parseAmount(offering.amountRaised ?? offering.fundsCommitted);
  if (!Number.isFinite(amount)) return '';
  return formatCurrencyToShorthand(amount) || '';
};

/** Minimum-investment display rounded to the nearest whole dollar. */
const minDisplay = (offering: TransformedOffering): string => {
  const min = parseAmount(offering.minimumInvestmentAmount);
  if (!Number.isFinite(min) || min <= 0) return '';
  return formatCurrencyFlexible(Math.round(min));
};

/** Clamp a percent into 0–100 for the progress fill. */
const clampPct = (value?: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
};

/**
 * Truthful progress fill (0–100) from amountRaised / maximumFundingAmount, or
 * null when the raise ceiling isn't available. The tombstones endpoint's
 * `percent_completed` is computed against the funding MINIMUM (a tiny goal),
 * so it exceeds 100 for nearly every offering and clamps to a false "100%
 * filled" — only `maximumFundingAmount` yields a real bar. Dormant until the
 * backend adds `maximum_funding_amount` to /tombstones (PD-36463).
 */
const realFillPct = (offering: TransformedOffering): number | null => {
  const raised = parseAmount(offering.amountRaised ?? offering.fundsCommitted);
  const max = parseAmount(offering.maximumFundingAmount);
  if (!Number.isFinite(raised) || !Number.isFinite(max) || max <= 0) return null;
  return clampPct((raised / max) * 100);
};

/**
 * Build a `Company` record from a live offering. The slug is the stable key so
 * the SE3 card resolves `COMPANIES[card.co]` against this live map.
 */
const toCompany = (
  offering: TransformedOffering,
  amountVisibility: AmountVisibilityContext,
): HomepageV3LiveCompany | null => {
  const key = offering.slug;
  const name = offering.name;
  if (!key || !name) return null;

  const reg = offering.regulationType;
  const crowd = isRegCf(reg) || isRegA(reg);
  const min = parseAmount(offering.minimumInvestmentAmount);

  return {
    key,
    name,
    series: seriesLine(offering),
    companyName: offering.companyName,
    // Private series use the logo + series title as their identity. An industry
    // line belongs to crowdfunding cards only; rendering it for Reg D made the
    // filtered Trending grid mix two distinct private-card treatments.
    sector: crowd ? sectorLine(offering) : '',
    invested: shouldShowAmountRaised({
      slug: offering.slug,
      amountRaised: offering.amountRaised ?? offering.fundsCommitted,
      ...amountVisibility,
    })
      ? investedDisplay(offering)
      : '',
    desc: offering.content || '',
    reg: regLine(reg),
    regcf: isRegCf(reg),
    regA: isRegA(reg),
    regD: isRegD(reg),
    // Preserve both live assets: control uses the company logo for private
    // cards, while the B/C image-first variants use the offering tombstone.
    // Crowdfunding continues to render the cover as its banner.
    logo:
      (crowd ? offering.coverTombstoneUrl : offering.logoUrl || offering.coverTombstoneUrl) ||
      undefined,
    tombstoneImage: offering.coverTombstoneUrl || undefined,
    banner: crowd && Boolean(offering.coverTombstoneUrl),
    fit: !crowd && offering.logoUrl ? 'contain' : 'cover',
    href: `/offering/${key}`,
    ...(Number.isFinite(min) && min > 0 ? { minimumAmount: min } : {}),
  };
};

type FillCls = 'ok' | 'urg' | 'amber';

/**
 * Days remaining for an offering. Prefer the explicit daysLeft (days_left);
 * when the /tombstones payload omits it, derive from the funding end date —
 * this is exactly what control does (useOfferingDaysLeft) and why the editorial
 * "X DAYS LEFT" closing-soon badge was missing while control showed it (QA).
 */
const resolveDaysLeft = (offering: TransformedOffering): number | undefined => {
  const explicit = offering.daysLeft;
  if (typeof explicit === 'number' && Number.isFinite(explicit) && explicit >= 0) {
    return explicit;
  }
  if (offering.endDate) {
    const end = new Date(offering.endDate).getTime();
    if (Number.isFinite(end)) {
      const days = Math.ceil((end - Date.now()) / 86_400_000);
      if (days >= 0) return days;
    }
  }
  return undefined;
};

/** Figures + fill for a card, derived from live progress / days-left / minimum. */
const toCardFigures = (
  offering: TransformedOffering,
  amountVisibility: AmountVisibilityContext,
): Pick<OfferingCardData, 'figL' | 'figR' | 'fill' | 'meta'> => {
  const realFill = realFillPct(offering);
  const hasRealFill = realFill !== null;
  const pct = realFill ?? 0;
  // The endDate fallback is reserved for the closing-soon badge so the card
  // figure remains an availability or minimum-investment metric.
  const days = offering.daysLeft;
  const urgent = (typeof days === 'number' && days <= 7) || (hasRealFill && pct >= 85);
  const cls: FillCls = urgent ? 'urg' : 'ok';

  const reg = offering.regulationType;
  const invested = shouldShowAmountRaised({
    slug: offering.slug,
    amountRaised: offering.amountRaised ?? offering.fundsCommitted,
    ...amountVisibility,
  })
    ? { lbl: 'Invested', val: investedDisplay(offering) }
    : undefined;
  if (isRegCf(reg) || isRegA(reg)) {
    // Crowdfunding (Reg CF / Reg A) tombstones omit the progress bar — a funding
    // % isn't a relevant signal for CF/A (Josh QA 2026-07-14). `meter` is left
    // off intentionally so OfferingCard renders no progress track.
    const min = minDisplay(offering);
    return {
      ...(invested ? { figL: invested } : {}),
      figR: min
        ? { lbl: 'Minimum', val: min, cls: 'amber' }
        : { lbl: 'Availability', val: 'Open', cls: 'ok' },
    };
  }
  // Reg D private series. A waitlisted fund (authoritative `lockedOnWaitlist`,
  // PD-36492) is effectively full — it reads "Availability / Waitlist" with a
  // full urgent bar; the gold "Waitlist" badge (toBadge) carries the waitlist
  // state. Otherwise, when the raise ceiling
  // (maximumFundingAmount) is known we show "Availability / X% left" + a truthful
  // bar; absent a ceiling the offering is treated as waitlisted with the full
  // meter. Reg D cards must never fall back to the crowdfunding minimum/open
  // presentation because their availability cannot be inferred from a funding
  // minimum.
  if (offering.lockedOnWaitlist) {
    return {
      ...(invested ? { figL: invested } : {}),
      figR: { lbl: 'Availability', val: 'Waitlist', cls: 'urg' },
      fill: { w: 100, cls: 'urg' },
    };
  }
  if (hasRealFill) {
    // When a Reg D private raise is fully subscribed (0% remaining) the figure
    // reads "Waitlist" rather than "0% left" — a full raise takes waitlist
    // signups (QA: "any Reg D at 0% left should say Waitlist"). The badge is
    // unaffected (toBadge still carries the gold "Waitlist" pill).
    const remaining = Math.max(0, 100 - pct);
    return {
      ...(invested ? { figL: invested } : {}),
      figR: { lbl: 'Availability', val: remaining === 0 ? 'Waitlist' : `${remaining}% left`, cls },
      fill: { w: pct, cls },
    };
  }
  return {
    ...(invested ? { figL: invested } : {}),
    figR: { lbl: 'Availability', val: 'Waitlist', cls: 'urg' },
    fill: { w: 100, cls: 'urg' },
  };
};

/** All tombstones with fewer than 30 days remaining show the days-left badge. */
const DAYS_LEFT_BADGE_EXCLUSIVE_MAX_DAYS = 30;

/**
 * On-card badge, per the Claude design. Precedence: "X days left" > "Waitlist".
 *
 * This is deliberately shelf- and regulation-agnostic: a tombstone that closes
 * in fewer than 30 days must show the same top-right badge everywhere it appears.
 * The lower card statistics remain invested plus the normal availability/minimum
 * metric; days remaining is never a lower statistic. Returns undefined when none
 * apply.
 */
const toBadge = (offering: TransformedOffering): OfferingCardData['badge'] | undefined => {
  const reg = offering.regulationType;
  const days = resolveDaysLeft(offering);
  const hasDays = typeof days === 'number' && days >= 0;
  const daysBadge = hasDays
    ? {
        cls: 'bdg-wine' as const,
        dot: true,
        label: `${days} ${days === 1 ? 'day' : 'days'} left`,
      }
    : undefined;

  if (daysBadge && (days as number) < DAYS_LEFT_BADGE_EXCLUSIVE_MAX_DAYS) {
    return daysBadge;
  }

  if (isRegCf(reg) || isRegA(reg)) return undefined;
  // PD-36492 — the authoritative `lockedOnWaitlist` (BE) also drives the badge.
  if (offering.waitlist || offering.lockedOnWaitlist) {
    return { cls: 'bdg-gold', label: 'Waitlist' };
  }
  return undefined;
};

/**
 * Under-card label (PD-35974), rendered via the card `meta` pill. Only emitted
 * when an eligibility flag is present on the offering; suppressed entirely for
 * crowdfunding (Reg CF / Reg A). Returns undefined when no flag applies (the
 * backend does not yet provide these — graceful no-op until it does).
 */
const toMeta = (offering: TransformedOffering): string | undefined => {
  const reg = offering.regulationType;
  if (isRegCf(reg) || isRegA(reg)) return undefined;

  if (offering.qpExclusive) return 'Qualified Purchasers Exclusive';
  if (offering.iraExclusive) return 'IRA Exclusive';
  if (offering.soldOut) return 'Previously Sold Out';
  return undefined;
};

/** Display names from an offering's tombstone `categories` (mixed shapes). */
const offeringCategoryNames = (offering: TransformedOffering): string[] => {
  const raw = offering.categories;
  if (!Array.isArray(raw)) return [];
  const names = raw
    .map((c) => (typeof c === 'string' ? c : (c?.name ?? c?.label ?? '')))
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set(names));
};

const toCard = (
  offering: TransformedOffering,
  spec: ShelfSpec,
  eligibilityBadges?: Record<string, string>,
  amountVisibility: AmountVisibilityContext = { settingsLoaded: true },
): OfferingCardData | null => {
  if (!offering.slug || !offering.name) return null;
  const reg = offering.regulationType;
  const crowd = isRegCf(reg) || isRegA(reg);

  const { meta: figureMeta, ...figures } = toCardFigures(offering, amountVisibility);
  const badge = toBadge(offering);
  const eligibilityMeta = toMeta(offering);
  // A per-offering admin override (ELIGIBILITY_BADGES_FLAG) is the highest-
  // precedence meta and shows for any regulation (explicit ops intent). Else
  // PD-35974 — eligibility label wins over the figures-derived availability
  // meta ("Closing soon"); all metas/badges suppressed for CF.
  const overrideMeta = eligibilityBadges?.[offering.slug.toLowerCase()];
  const meta = overrideMeta ?? (crowd ? undefined : (eligibilityMeta ?? figureMeta));
  const categories = offeringCategoryNames(offering);

  return {
    co: offering.slug,
    ...figures,
    ...(crowd && figures.figL && Number(offering.numberOfInvestors) > 0
      ? { numberOfInvestors: Number(offering.numberOfInvestors) }
      : {}),
    ...(badge ? { badge } : {}),
    ...(meta ? { meta } : {}),
    ...(categories.length ? { categories } : {}),
  };
};

/**
 * How a shelf sources its offerings.
 *  - `tombstone`: a control row (`most_momentum` / `closing_soon`). When
 *    `se3Row` is set (PD-35981), that SE3 A/B ranking row is preferred when
 *    present and non-empty, falling back to `row` (the legacy control row)
 *    otherwise.
 *  - `lowMinimum`: FE synthesis (PD-35973) — crowdfunding-only, momentum-ranked.
 */
type ShelfSource =
  | { kind: 'tombstone'; row: keyof TransformedTombstones; se3Row?: keyof TransformedTombstones }
  | { kind: 'preIpo' }
  | { kind: 'lowMinimum' };

interface ShelfSpec {
  eyebrow: string;
  title: string;
  desc: string;
  source: ShelfSource;
  emphasizeDaysLeft: boolean;
  /** Ordering-disclosure tooltip shown next to the shelf title. */
  tip?: string;
}

const ORDERING_TIP_SUFFIX =
  'Ordering is automated, is not a recommendation, and does not reflect the merits, suitability, or likely performance of any offering.';

const SHELF_SPECS: ShelfSpec[] = [
  {
    eyebrow: 'Most momentum',
    title: 'Trending now',
    desc: 'Highest investment activity recently',
    source: { kind: 'tombstone', row: 'most_momentum', se3Row: 'most_momentum_se3' },
    emphasizeDaysLeft: false,
    tip: `Offerings here are ordered by most invested within the last 3 days. ${ORDERING_TIP_SUFFIX}`,
  },
  {
    eyebrow: 'Private markets',
    title: 'Pre-IPO Deals',
    desc: 'Private-market offerings for accredited investors',
    source: { kind: 'preIpo' },
    emphasizeDaysLeft: false,
  },
  {
    eyebrow: 'Limited availability',
    title: 'Closing soon',
    desc: 'Due to least inventory or closing offering soon',
    source: { kind: 'tombstone', row: 'closing_soon', se3Row: 'closing_soon_se3' },
    emphasizeDaysLeft: true,
    tip: `Offerings here are ordered by time remaining in the offering or remaining availability. ${ORDERING_TIP_SUFFIX}`,
  },
  {
    eyebrow: 'Accessible investing',
    title: 'Low minimum offerings',
    desc: 'Crowdfunding offerings ranked by recent activity',
    source: { kind: 'lowMinimum' },
    emphasizeDaysLeft: false,
    tip: `Offerings here are ordered by most invested within the last 3 days, excluding private offerings. ${ORDERING_TIP_SUFFIX}`,
  },
];

const MAX_CARDS_PER_SHELF = 4;

type PrivateShelfOfferings = Pick<
  PrivatePageOfferings,
  'availableCards' | 'availableCompanies' | 'hrefByCo' | 'hasLiveData'
>;

/**
 * Add the authoritative `/private` open-offering feed to the shared Pre-IPO
 * shelf. The primary homepage tombstone rows remain a fallback, but do not
 * contain private offerings in every environment (notably stage).
 */
export const mergePrivatePreIpoOfferings = (
  base: Omit<HomepageV3Offerings, 'isLoading'>,
  privateOfferings: PrivateShelfOfferings,
  {
    excludeRegD = false,
    hiddenSlugs = [],
    eligibilityBadges = {},
  }: {
    excludeRegD?: boolean;
    hiddenSlugs?: string[];
    eligibilityBadges?: Record<string, string>;
  } = {},
): Omit<HomepageV3Offerings, 'isLoading'> => {
  const preIpoIndex = base.sections.findIndex((section) => section.title === 'Pre-IPO Deals');
  if (preIpoIndex < 0) return base;

  const hidden = new Set(hiddenSlugs.map((slug) => slug.trim().toLowerCase()).filter(Boolean));
  const acceptedPrivateCards = excludeRegD
    ? []
    : privateOfferings.availableCards
        .filter((card) => !isHiddenSlug(card.co) && !hidden.has(card.co.toLowerCase()))
        .map((card) => {
          const overrideMeta = eligibilityBadges[card.co.toLowerCase()];
          return overrideMeta ? { ...card, meta: overrideMeta } : card;
        });

  const searchSeen = new Set<string>();
  const searchSections = base.searchSections.map((section, index) => {
    if (index !== 0) return section;
    const cards = [...section.cards, ...acceptedPrivateCards].filter((card) => {
      if (searchSeen.has(card.co)) return false;
      searchSeen.add(card.co);
      return true;
    });
    return { ...section, cards };
  });

  const fullSections = base.fullSections.map((section) => {
    if (section.title !== 'Pre-IPO Deals') return section;
    const fullSeen = new Set<string>();
    const cards = [...acceptedPrivateCards, ...section.cards].filter((card) => {
      if (fullSeen.has(card.co)) return false;
      fullSeen.add(card.co);
      return true;
    });
    return { ...section, cards };
  });

  const acceptedCos = new Set(acceptedPrivateCards.map((card) => card.co));
  const acceptedCompanies = Object.fromEntries(
    Object.entries(privateOfferings.availableCompanies).filter(([co]) => acceptedCos.has(co)),
  );
  const acceptedHrefs = Object.fromEntries(
    Object.entries(privateOfferings.hrefByCo).filter(([co]) => acceptedCos.has(co)),
  );
  return {
    ...base,
    sections: allocatePreviewSections(fullSections),
    searchSections,
    fullSections,
    companies: { ...base.companies, ...acceptedCompanies },
    hrefByCo: { ...base.hrefByCo, ...acceptedHrefs },
    hasLiveData: base.hasLiveData || acceptedPrivateCards.length > 0,
  };
};

const SEARCH_SPEC: ShelfSpec = {
  eyebrow: 'Search',
  title: 'All live offerings',
  desc: 'Current offerings matching your search',
  source: { kind: 'lowMinimum' },
  emphasizeDaysLeft: false,
};

/**
 * PD-35973 — Low-minimum shelf as a crowdfunding-only (Reg CF / Reg A) variant
 * of Trending. It starts with the preferred SE3 momentum ranking, continues
 * through the legacy momentum ranking, then removes private offerings. The
 * complete union is required because Trending may claim every SE3 candidate
 * before the Low minimum preview is built.
 */
const buildLowMinimumPool = (data: TransformedTombstones): TransformedOffering[] => {
  const isCrowd = (o: TransformedOffering): boolean =>
    isRegCf(o.regulationType) || isRegA(o.regulationType);

  const ordered: TransformedOffering[] = [
    ...(data.most_momentum_se3 ?? []),
    ...(data.most_momentum ?? []),
  ];

  const seen = new Set<string>();
  const pool: TransformedOffering[] = [];
  ordered.forEach((offering) => {
    if (!offering.slug || !offering.name || !isCrowd(offering)) return;
    if (seen.has(offering.slug)) return;
    seen.add(offering.slug);
    pool.push(offering);
  });
  return pool;
};

export const buildPreIpoPool = (data: TransformedTombstones): TransformedOffering[] => {
  const ordered: TransformedOffering[] = [
    ...(data.most_momentum_se3 ?? []),
    ...(data.most_momentum ?? []),
    ...(data.most_popular ?? []),
    ...(data.newest ?? []),
    ...(data.closing_soon_se3 ?? []),
    ...(data.closing_soon ?? []),
  ];

  const seen = new Set<string>();
  return ordered.filter((offering) => {
    if (!offering.slug || !offering.name || !isRegD(offering.regulationType)) return false;
    if (seen.has(offering.slug)) return false;
    seen.add(offering.slug);
    return true;
  });
};

/**
 * Resolve the candidate offerings for a shelf, before dedup + cap.
 *
 * PD-35981 — for tombstone shelves, prefer the SE3 A/B ranking row (`se3Row`)
 * when it is present and non-empty. PD-36215 — if that row has fewer than the
 * visible shelf cap, top it up from the legacy control row so Explore can still
 * render four cards before "View all".
 */
const shelfCandidates = (spec: ShelfSpec, data: TransformedTombstones): TransformedOffering[] => {
  if (spec.source.kind === 'lowMinimum') return buildLowMinimumPool(data);
  if (spec.source.kind === 'preIpo') return buildPreIpoPool(data);
  const { se3Row, row } = spec.source;
  const se3Candidates = se3Row ? data[se3Row] : undefined;
  const fallbackCandidates = data[row] ?? [];
  if (!se3Candidates || se3Candidates.length === 0) return fallbackCandidates;
  if (se3Candidates.length >= MAX_CARDS_PER_SHELF) return se3Candidates;

  const seen = new Set(se3Candidates.map((offering) => offering.slug).filter(Boolean));
  const topUps = fallbackCandidates.filter((offering) => {
    if (!offering.slug) return true;
    if (seen.has(offering.slug)) return false;
    seen.add(offering.slug);
    return true;
  });

  return [...se3Candidates, ...topUps];
};

const buildSearchPool = (data: TransformedTombstones): TransformedOffering[] => {
  const ordered: TransformedOffering[] = [
    ...(data.most_momentum_se3 ?? []),
    ...(data.closing_soon_se3 ?? []),
    ...(data.most_momentum ?? []),
    ...(data.most_popular ?? []),
    ...(data.newest ?? []),
    ...(data.closing_soon ?? []),
  ];

  const seen = new Set<string>();
  const pool: TransformedOffering[] = [];
  ordered.forEach((offering) => {
    if (!offering.slug || !offering.name || seen.has(offering.slug)) return;
    seen.add(offering.slug);
    pool.push(offering);
  });
  return pool;
};

const SPOTLIGHT_RISK =
  ' This investment is speculative, illiquid, and involves a high degree of risk, including the possible loss of your entire investment.';

/** Reg-aware featured-offering disclosure, matching the tombstone card wording. */
const spotlightDisclosure = (offering: TransformedOffering): string => {
  const name = stringValue(offering.name)?.trim() || 'the underlying company';
  const reg = offering.regulationType;
  if (isRegA(reg)) {
    return `This is a Regulation A+ offering of common stock in ${name}, conducted via StartEngine Primary, LLC. You are buying ${name} shares directly.${SPOTLIGHT_RISK}`;
  }
  if (isRegCf(reg)) {
    return `This is a Regulation Crowdfunding offering of securities in ${name}, conducted via StartEngine Primary, LLC.${SPOTLIGHT_RISK}`;
  }
  return `${regDDisclosure(stringValue(offering.companyName))}${SPOTLIGHT_RISK}`;
};

/**
 * Pick a featured spotlight offering from the live set: the first Reg D
 * ("private") offering in `most_momentum`, else the first offering there, else
 * the first offering anywhere. Keeps the editorial spotlight design.
 */
const pickSpotlight = (
  transformed: TransformedTombstones,
  featuredOffering: TransformedOffering | null | undefined,
  deferFallback: boolean,
): { spotlight: Spotlight | null; href?: string; image?: string } => {
  if (featuredOffering?.slug && featuredOffering.name) {
    return {
      href: `/offering/${featuredOffering.slug}`,
      image: featuredOffering.coverTombstoneUrl || undefined,
      spotlight: {
        eyebrowLabel: 'Featured Offering',
        series: regLine(featuredOffering.regulationType),
        name: featuredOffering.name,
        desc: featuredOffering.content || `Invest in ${featuredOffering.name} on StartEngine.`,
        cta: 'View details',
        disclosureLegal: spotlightDisclosure(featuredOffering),
      },
    };
  }

  if (deferFallback) {
    return { spotlight: null };
  }

  const momentum = transformed.most_momentum ?? [];
  const candidate =
    momentum.find((o) => isRegD(o.regulationType) && o.slug && o.name) ||
    momentum.find((o) => o.slug && o.name) ||
    (transformed.most_popular ?? []).find((o) => o.slug && o.name) ||
    (transformed.closing_soon ?? []).find((o) => o.slug && o.name) ||
    (transformed.newest ?? []).find((o) => o.slug && o.name) ||
    null;

  if (!candidate || !candidate.slug || !candidate.name) {
    return { spotlight: null };
  }

  return {
    href: `/offering/${candidate.slug}`,
    image: candidate.coverTombstoneUrl || undefined,
    spotlight: {
      eyebrowLabel: 'Featured Offering',
      series: regLine(candidate.regulationType),
      name: candidate.name,
      desc: candidate.content || `Invest in ${candidate.name} on StartEngine.`,
      cta: 'View details',
      disclosureLegal: spotlightDisclosure(candidate),
    },
  };
};

export const buildHomepageV3Offerings = (
  transformed: TransformedTombstones | undefined,
  {
    featuredOffering,
    deferSpotlightFallback = false,
    hiddenSlugs = [],
    excludeRegD = false,
    eligibilityBadges = {},
    amountVisibility = { settingsLoaded: true },
  }: {
    featuredOffering?: TransformedOffering | null;
    deferSpotlightFallback?: boolean;
    /**
     * Extra slugs to hide, on top of the NEXT_PUBLIC_HIDDEN_OFFERING_SLUGS env
     * kill-switch. Sourced by the hook from a PostHog flag payload
     * (`se3-hidden-offering-slugs`) so ops can pull an offering — e.g. one in a
     * VC early-access window (Paige QA) — dynamically, with no deploy.
     */
    hiddenSlugs?: string[];
    /**
     * Drop Reg D offerings from every shelf. Set for retail (logged-in,
     * non-accredited) users, who should see only Reg CF / Reg A offerings —
     * Reg D (506c) is accredited-only (QA).
     */
    excludeRegD?: boolean;
    /**
     * Per-offering "above body" badge overrides (slug -> label), from the
     * ELIGIBILITY_BADGES_FLAG PostHog payload. Lets ops add QP/IRA/Sold-Out
     * badges to a tombstone with no deploy.
     */
    eligibilityBadges?: Record<string, string>;
    amountVisibility?: AmountVisibilityContext;
  } = {},
): Omit<HomepageV3Offerings, 'isLoading'> => {
  // Hide offerings that shouldn't be publicly browsable yet. Two levers, same
  // effect: the env kill-switch (via filterHiddenOfferings — shared with search /
  // offering page / sitemap) and a dynamic PostHog payload list (`hiddenSlugs`).
  // Backend `hidden` field is the cross-platform long-term fix (PD-35613).
  const extraHidden = new Set(hiddenSlugs.map((s) => s.trim().toLowerCase()).filter(Boolean));
  const isVisible = (o: TransformedOffering): boolean =>
    typeof o?.slug !== 'string' || !extraHidden.has(o.slug.toLowerCase());
  // Retail users see only Reg CF / Reg A — never Reg D.
  const passesRegFilter = (o: TransformedOffering): boolean =>
    !excludeRegD || !isRegD(o.regulationType);
  const data = Object.fromEntries(
    Object.entries(transformed ?? {}).map(([key, value]) => [
      key,
      Array.isArray(value)
        ? filterHiddenOfferings(value).filter(isVisible).filter(passesRegFilter)
        : value,
    ]),
  ) as TransformedTombstones;
  const visibleFeatured =
    featuredOffering &&
    !isHiddenSlug(featuredOffering.slug) &&
    isVisible(featuredOffering) &&
    passesRegFilter(featuredOffering)
      ? featuredOffering
      : null;
  const companies: Record<string, HomepageV3LiveCompany> = {};
  const hrefByCo: Record<string, string> = {};
  const sections: OfferingSection[] = [];
  const searchCards: OfferingCardData[] = [];
  const previewClaimedSlugs = new Set<string>();
  // Broader live pool used to top up a retail shelf that Reg D removal left
  // short of the 4-up grid.
  const backfillPool = buildSearchPool(data);

  SHELF_SPECS.forEach((spec) => {
    const cards: OfferingCardData[] = [];
    const seen = new Set<string>();
    const addOffering = (offering: TransformedOffering) => {
      if (cards.length >= MAX_CARDS_PER_SHELF) return;
      if (!offering.slug || seen.has(offering.slug) || previewClaimedSlugs.has(offering.slug))
        return;
      const company = toCompany(offering, amountVisibility);
      const card = toCard(offering, spec, eligibilityBadges, amountVisibility);
      if (!company || !card) return;
      seen.add(offering.slug);
      previewClaimedSlugs.add(offering.slug);
      companies[company.key] = company;
      if (company.href) hrefByCo[company.key] = company.href;
      cards.push(card);
    };

    shelfCandidates(spec, data).forEach(addOffering);
    // Retail (non-accredited) removes Reg D from the momentum/closing rows, which
    // can leave a curated shelf with only three cards and a visible gap where a
    // private offering was — fill it with the next eligible offering instead of
    // showing a short grid (Paige QA 2026-07-15). Scoped to retail so the
    // accredited/default shelf composition is unchanged; the Low minimum shelf
    // already draws from the whole crowdfunding pool.
    if (excludeRegD && cards.length < MAX_CARDS_PER_SHELF && spec.source.kind === 'tombstone') {
      backfillPool.forEach(addOffering);
    }

    sections.push({
      eyebrow: spec.eyebrow,
      title: spec.title,
      desc: spec.desc,
      cards,
      tip: spec.tip,
    });
  });

  buildSearchPool(data).forEach((offering) => {
    const company = toCompany(offering, amountVisibility);
    const card = toCard(offering, SEARCH_SPEC, eligibilityBadges, amountVisibility);
    if (!company || !card) return;
    companies[company.key] = company;
    if (company.href) hrefByCo[company.key] = company.href;
    searchCards.push(card);
  });

  const searchSections: OfferingSection[] = [
    {
      eyebrow: SEARCH_SPEC.eyebrow,
      title: SEARCH_SPEC.title,
      desc: SEARCH_SPEC.desc,
      cards: searchCards,
    },
  ];

  // PD-36468 — uncapped per-category shelves for "View all". Same specs as the
  // homepage `sections` but no cap and no cross-shelf dedup, so each category is
  // complete (an offering may legitimately appear under more than one category).
  const fullSections: OfferingSection[] = SHELF_SPECS.map((spec) => {
    const seen = new Set<string>();
    const cards: OfferingCardData[] = [];
    shelfCandidates(spec, data).forEach((offering) => {
      if (!offering.slug || seen.has(offering.slug)) return;
      const company = toCompany(offering, amountVisibility);
      const card = toCard(offering, spec, eligibilityBadges, amountVisibility);
      if (!company || !card) return;
      seen.add(offering.slug);
      companies[company.key] = company;
      if (company.href) hrefByCo[company.key] = company.href;
      cards.push(card);
    });
    return { eyebrow: spec.eyebrow, title: spec.title, desc: spec.desc, cards, tip: spec.tip };
  });

  const hasLiveData =
    sections.some((section) => section.cards.length > 0) || searchCards.length > 0;
  const {
    spotlight,
    href: spotlightHref,
    image: spotlightImage,
  } = pickSpotlight(data, visibleFeatured, deferSpotlightFallback);

  return {
    sections,
    searchSections,
    fullSections,
    companies,
    eligibilityBadges,
    spotlight,
    spotlightHref,
    spotlightImage,
    hrefByCo,
    hasLiveData,
  };
};

/**
 * useHomepageV3Offerings — live offerings for the SE3 editorial homepage,
 * mapped into the `OfferingCard` shape. Falls back gracefully (consumers use
 * demoData) when `hasLiveData` is false.
 */
interface UseHomepageV3OfferingsOptions {
  enabled?: boolean;
  fetchHomepageFeaturedOffering?: boolean;
  /** Retail (logged-in, non-accredited): drop Reg D from every shelf. */
  excludeRegD?: boolean;
}

export default function useHomepageV3Offerings({
  enabled = true,
  fetchHomepageFeaturedOffering = true,
  excludeRegD = false,
}: UseHomepageV3OfferingsOptions = {}): HomepageV3Offerings {
  const { data, isLoading } = useGetCurrentOfferingsTombstones({ enabled });
  const { data: showAmountRaisedSlugs, isSuccess: showAmountSettingsLoaded } =
    useShowAmountRaisedList();
  const { data: excludeAmountOfferingSlugs, isSuccess: excludeAmountSettingsLoaded } =
    useExcludeAmountOfferingList();
  const hiddenSlugsPayload = useSafeFeatureFlagPayload(HIDDEN_SLUGS_FLAG);
  const eligibilityBadgesPayload = useSafeFeatureFlagPayload(ELIGIBILITY_BADGES_FLAG);
  const privateOfferings = usePrivatePageOfferings({ enabled });
  const {
    data: featuredOfferingData,
    isLoading: isFeaturedOfferingLoading,
    isFetching: isFeaturedOfferingFetching,
  } = useGetOffering({
    slug: HOMEPAGE_FEATURED_OFFERING_SLUG,
    enabled: enabled && fetchHomepageFeaturedOffering,
  });

  return useMemo(() => {
    if (!enabled) {
      return { ...buildHomepageV3Offerings(undefined), isLoading: false };
    }
    const transformed = data
      ? (transformPrimaryOfferings(data) as TransformedTombstones)
      : undefined;
    const featuredOffering = fetchHomepageFeaturedOffering
      ? normalizeFetchedFeaturedOffering(featuredOfferingData)
      : undefined;
    const isFeaturedPending = isFeaturedOfferingLoading || isFeaturedOfferingFetching;
    const amountVisibility: AmountVisibilityContext = {
      showAmountRaisedSlugs,
      excludeAmountOfferingSlugs,
      settingsLoaded: showAmountSettingsLoaded && excludeAmountSettingsLoaded,
    };
    const homepageOfferings = buildHomepageV3Offerings(transformed, {
      featuredOffering,
      deferSpotlightFallback: fetchHomepageFeaturedOffering && isFeaturedPending,
      hiddenSlugs: parseHiddenSlugs(hiddenSlugsPayload),
      excludeRegD,
      eligibilityBadges: parseEligibilityBadges(eligibilityBadgesPayload),
      amountVisibility,
    });
    return {
      ...mergePrivatePreIpoOfferings(homepageOfferings, privateOfferings, {
        excludeRegD,
        hiddenSlugs: parseHiddenSlugs(hiddenSlugsPayload),
        eligibilityBadges: parseEligibilityBadges(eligibilityBadgesPayload),
      }),
      isLoading:
        isLoading ||
        privateOfferings.isLoading ||
        (fetchHomepageFeaturedOffering && isFeaturedPending),
      amountVisibility,
    };
  }, [
    data,
    enabled,
    eligibilityBadgesPayload,
    excludeRegD,
    featuredOfferingData,
    fetchHomepageFeaturedOffering,
    hiddenSlugsPayload,
    isFeaturedOfferingFetching,
    isFeaturedOfferingLoading,
    isLoading,
    privateOfferings,
    showAmountRaisedSlugs,
    showAmountSettingsLoaded,
    excludeAmountOfferingSlugs,
    excludeAmountSettingsLoaded,
  ]);
}
